import React from 'react';

const Invoices: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Invoices Management</h1>
        <p className="text-gray-600">
          An invoice is a formal document issued by a seller to a buyer, requesting payment for goods or services provided.
          It serves as both a request for payment and a legal record of the transaction.
        </p>
      </div>

      {/* Invoice Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Invocie Overview</h2>
        <p className="text-gray-600 mb-4">
          Invocie in DAS represent amounts owed to customer for goods or services received. Invoices can be created
          directly or generated from sale orders when items are received. The system manages the complete
          invoice lifecycle from creation to payment processing and customer credit management.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/invoices/list.png"
            alt="Invoice List View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Features</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Direct invoice creation and   </li>
              <li>Sale sale order to invoice conversion</li>
              <li>Payment recording and tracking</li>
              <li>Customer credit application</li>
              <li>Invoice editing and void functionality</li>
              <li>Batch tracking for inventory items</li>
              <li>PDF generation and document management</li>
              <li>Journal entry automation</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Status Types</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Draft:</strong> Invoice being prepared</li>
              <li><strong>Confirmed:</strong> Invoice finalized and ready for payment</li>
              <li><strong>Partial Paid:</strong> Partially paid invoice</li>
              <li><strong>Paid:</strong> Fully paid invoice</li>
              <li><strong>Void:</strong> Cancelled invoice</li>
            </ul>
          </div>
        </div>
      </div>



      {/* Search and Filtering */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Search and Filtering Invoice</h2>
        <p className="text-gray-600 mb-4">
          Efficiently locate and manage invoices using comprehensive search and filtering options with multiple criteria.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/invoices/search-filter.png"
            alt="Invoice Search Dialog"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Advanced Search Dialog</h3>
        <p className="text-gray-600 mb-4">
          Use the comprehensive search dialog to find specific invoice with multiple filter criteria:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Identification Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Invoice #:</strong> Search by specific invoice number</li>
              <li><strong>Reference #:</strong> Find by customer's invoice reference</li>
              <li><strong>Batch Number:</strong> Filter by product batch numbers</li>
              <li><strong>Sales Order #:</strong> Find invoice from specific POs</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Date and Entity Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Invoice Date Range:</strong> Filter by invoice creation dates</li>
              <li><strong>Due Date Range:</strong> Filter by payment due dates</li>
              <li><strong>customer:</strong> Search by customer with lookup function</li>
              <li><strong>Warehouse:</strong> Filter by delivery warehouse</li>
            </ul>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Status and Branch Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Status:</strong> Filter by Draft, Confirmed, Partial Paid, Paid, Void</li>
              <li><strong>Branch:</strong> Filter by organizational branch</li>
              <li><strong>Multiple Criteria:</strong> Combine filters for precise results</li>
              <li><strong>Search and Cancel:</strong> Execute search or clear criteria</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Search Benefits:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Quickly locate invoices for payment processing</li>
              <li>Find invoices by customer for vendor management</li>
              <li>Filter by date ranges for reporting periods</li>
              <li>Search by batch numbers for inventory tracking</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Quick Search with Search Icon</h3>
        <p className="text-gray-600 mb-4">
          Use the search icon (🔍) in the top-right corner for instant invoice filtering:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Real-time Search:</strong> Filter invoices as you type</li>
          <li><strong>Multi-field Search:</strong> Searches across invoice numbers, customers, and references</li>
          <li><strong>Quick Access:</strong> No separate forms needed for basic searches</li>
          <li><strong>Immediate Results:</strong> Instant table filtering and updates</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Search Use Cases</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Payment Management:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Find overdue invoices by due date range</li>
              <li>Locate invoices ready for payment processing</li>
              <li>Search for specific customer invoices</li>
              <li>Filter by payment status</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Reporting and Audit:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Generate reports for specific date ranges</li>
              <li>Find invoices for audit purposes</li>
              <li>Track invoices by warehouse or branch</li>
              <li>Verify sale order to invoice conversions</li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Search Tip:</strong> Use the advanced search dialog for complex queries with multiple criteria,
            and the quick search icon for simple, immediate filtering. Combine different search fields to narrow
            down results and find exactly the invoices you need for efficient accounts payable management.
          </p>
        </div>
      </div>

      {/* Creating and Editing Invoices */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating and Editing Invoices</h2>
        <p className="text-gray-600 mb-4">
          Invoices can be created directly (when allowed) or generated from sale orders. The invoice creation
          process includes detailed product information, batch tracking, and financial calculations.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/invoices/edit-form.png"
            alt="Edit Invoice Form"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Invoice Header Information</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Basic Details:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Customer:</strong> Select vendor with search functionality</li>
              <li><strong>Branch:</strong> Organizational branch assignment</li>
              <li><strong>Sales Order #:</strong> Link to originating PO</li>
              <li><strong>Invoice Date:</strong> Date of invoice creation</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Additional Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Reference #:</strong> Customer's invoice reference</li>
              <li><strong>Payment Terms:</strong> Payment conditions</li>
              <li><strong>Currency:</strong> Transaction currency</li>
              <li><strong>Warehouse:</strong> Delivery location</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Product Details and Batch Tracking</h3>
        <p className="text-gray-600 mb-4">
          Invoice support detailed product information with advanced batch tracking capabilities:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Product Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Product Details:</strong> Select items from inventory</li>
              <li><strong>Account:</strong> Chart of accounts classification</li>
              <li><strong>Quantity:</strong> Received quantity</li>
              <li><strong>Rate:</strong> Unit price</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Batch Management:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Batch Number:</strong> Lot identification</li>
              <li><strong>Stock on Hand:</strong> Current inventory levels</li>
              <li><strong>Batch Expiry Date:</strong> Expiration tracking</li>
              <li><strong>Auto-generated Batches:</strong> System-created batch numbers</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Financial Calculations</h3>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Discount Management:</strong> Percentage or fixed amount discounts</li>
          <li><strong>Tax Calculations:</strong> Automatic tax computation</li>
          <li><strong>Sub Total and Total:</strong> Automatic amount calculations</li>
          <li><strong>Currency Handling:</strong> Multi-currency support</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Notes and Attachments</h3>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Notes Section:</strong> Additional invoice information and comments</li>
          <li><strong>File Attachments:</strong> Upload supporting documents</li>
          <li><strong>File Size Limits:</strong> Maximum file size restrictions</li>
          <li><strong>Document Management:</strong> Organized file storage</li>
        </ul>
      </div>

      {/* Invoice Detail View and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Invoice Detail View and Management</h2>
        <p className="text-gray-600 mb-4">
          The invoice detail view provides comprehensive information and access to all invoice management functions.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/invoices/detail.png"
            alt="invoice Detail View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Action Buttons and Functions</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Primary Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Edit:</strong> Modify invoice details (when allowed)</li>
              <li><strong>PDF/Print:</strong> Generate invoice documents</li>
              <li><strong>Record Payment:</strong> Process payment against invoice</li>
              <li><strong>Apply Credits:</strong> Use customer credits</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>More Actions Menu:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Clone:</strong> Create similar invoices</li>
              <li><strong>Confirm Invoice:</strong> Finalize draft invoices</li>
              <li><strong>Void Invoice:</strong> Cancel invoices</li>
              <li><strong>Delete:</strong> Remove invoices</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Invoice Information Display</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Header Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Invoice number and date</li>
              <li>Due date and payment terms</li>
              <li>Batch number and PO reference</li>
              <li>Customer details and contact information</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Financial Summary:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Sub total and tax amounts</li>
              <li>Total invoice amount</li>
              <li>Remaining balance due</li>
              <li>Payment history and credits applied</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Payment Management */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Management</h2>
        <p className="text-gray-600 mb-4">
          Manage invoice payments through direct payment recording and customer credit applications.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/invoices/payment-form.png"
            alt="Payment Recording Form"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Recording Payments</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Payment Details:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Branch:</strong> Payment processing branch</li>
              <li><strong>Date:</strong> Payment date</li>
              <li><strong>Paid Through:</strong> Payment method/account</li>
              <li><strong>Payment Mode:</strong> Cash, check, transfer, etc.</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Payment Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Reference #:</strong> Payment reference number</li>
              <li><strong>Amount:</strong> Payment amount in invoice currency</li>
              <li><strong>Bank Charges:</strong> Associated bank fees</li>
              <li><strong>Notes:</strong> Payment notes and comments</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Customer Credit Application</h3>
        <div className="mb-6">
          <img
            src="/images/sales/invoices/apply-credit-form.png"
            alt="Apply Credits Dialog"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <p className="text-gray-600 mb-4">
          Apply existing customer credits to reduce invoice amounts:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Credit Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Credit Details:</strong> Credit note numbers and dates</li>
              <li><strong>Branch:</strong> Credit originating branch</li>
              <li><strong>Amount:</strong> Total credit amount available</li>
              <li><strong>Available Balance:</strong> Unused credit balance</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Application Process:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Amount Applied:</strong> Credit amount to apply</li>
              <li><strong>Exchange Rate:</strong> Currency conversion if needed</li>
              <li><strong>Remaining Invoice Balance:</strong> Balance after credit application</li>
              <li><strong>Final Invoice Balance:</strong> Updated total due</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Invoice Tracking and Integration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Invoice Tracking and Integration</h2>
        <p className="text-gray-600 mb-4">
          Track invoice relationships with payments, sale orders, and view detailed transaction history.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Payment and Sale Order Tabs</h3>
        <div className="mb-6">
          <img
            src="/images/sales/invoices/payments-tab.png"
            alt="Payments Made Tab"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Payments Made Tab:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Complete payment history for the invoice</li>
              <li>Payment dates and reference numbers</li>
              <li>Payment modes and amounts</li>
              <li>Remaining balance tracking</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Sale Order Tab:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Link to originating sale order</li>
              <li>Sales order details and status</li>
              <li>Order-to-invoice conversion tracking</li>
              <li>Procurement audit trail</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Sale Order Tracking</h3>
        <div className="mb-6">
          <img
            src="/images/sales/invoices/sale-order-form.png"
            alt="Credits Applied Tab"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <p className="text-gray-600 mb-4">
          Monitor all customer credits applied to invoices:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Sales  Orders:</strong> Sale Order Id no </li>
          <li><strong>Date:</strong> Sale Order Create Date </li>
          <li><strong>Status:</strong></li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Journal Entry Integration</h3>
        <div className="mb-6">
          <img
            src="/images/sales/invoices/journal.png"
            alt="Journal Entries"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <p className="text-gray-600 mb-4">
          View automatic journal entries generated from invoice transactions:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Account Entries:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Accounts Payable:</strong> Credit entries for amounts owed</li>
              <li><strong>Tax Payable:</strong> Tax liability entries</li>
              <li><strong>Inventory Asset:</strong> Debit entries for received goods</li>
              <li><strong>Expense Accounts:</strong> Direct expense postings</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Journal Features:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Automatic posting upon invoice confirmation</li>
              <li>Debit and credit balance verification</li>
              <li>Multi-currency support and conversion</li>
              <li>Audit trail for all journal postings</li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Integration Note:</strong> Invoices automatically generate journal entries that update
            accounts payable, inventory, and expense accounts. This ensures accurate financial reporting
            and maintains proper accounting records for all customer transactions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Invoices;
